/**
 * ProcedureDocumentationModal
 * Nurse documents an injection or CLIA-waived lab.
 * Dynamically renders fields based on procedure type.
 * On submit → charge drops + note updates automatically.
 */
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Syringe, FlaskConical, Check, X, Loader2, ChevronDown, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const INJECTION_ROUTES = ['IM', 'SubQ', 'Intradermal', 'Intra-articular', 'IV Push', 'Local infiltration'];
const INJECTION_SITES  = [
  'Right Deltoid', 'Left Deltoid',
  'Right Gluteus', 'Left Gluteus',
  'Right Thigh', 'Left Thigh',
  'Abdomen', 'Right Forearm', 'Left Forearm', 'Other'
];

export default function ProcedureDocumentationModal({ encounterId, task, onClose, onDocumented }) {
  const [catalog, setCatalog]   = useState([]);
  const [procedure, setProcedure] = useState(null);
  const [form, setForm]         = useState({
    dose_given: '', units: 1,
    lot_number: '', expiration_date: '',
    route: '', site: '', nurse_notes: '',
    result_data: {},
  });
  const [submitting, setSubmitting] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);

  // Load catalog on mount and pre-select the ordered procedure
  useEffect(() => {
    api.get('/procedures/catalog').then(res => {
      const items = res.data.procedures || [];
      setCatalog(items);
      // Pre-select based on task name
      if (task?.procedure_name) {
        const match = items.find(p =>
          p.name.toLowerCase().includes(task.procedure_name.toLowerCase()) ||
          task.procedure_name.toLowerCase().includes(p.name.toLowerCase()) ||
          (task.j_code && p.billing_code === task.j_code)
        );
        if (match) selectProcedure(match);
      }
    }).catch(() => {});
    // Check if encounter already signed
    if (encounterId) {
      api.get(`/encounters/${encounterId}`).then(res => {
        const enc = res.data?.encounter || res.data;
        setAlreadySigned(enc?.status === 'signed');
      }).catch(() => {});
    }
  }, []);

  const selectProcedure = (p) => {
    setProcedure(p);
    setForm(prev => ({
      ...prev,
      dose_given: p.default_dose || '',
      units: p.default_units || 1,
      route: p.routes?.[0] || '',
      result_data: {},
    }));
  };

  const set = (field, val) => setForm(prev => ({ ...prev, [field]: val }));
  const setResult = (field, val) => setForm(prev => ({
    ...prev, result_data: { ...prev.result_data, [field]: val }
  }));

  const handleSubmit = async () => {
    if (!procedure) { toast.error('Select a procedure first'); return; }
    if (procedure.requires_lot && !form.lot_number) {
      toast.error('Lot number is required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/procedures/document/${encounterId}`, {
        procedure_catalog_id: procedure.id,
        ...form,
      });
      if (res.data.already_signed) {
        toast.success(`Documented — addendum created for physician to co-sign`);
      } else {
        toast.success(`${procedure.name} documented and charge dropped to billing`);
      }
      onDocumented?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Documentation failed');
    } finally { setSubmitting(false); }
  };

  const isInjection  = procedure?.category !== 'clia_lab';
  const isClia       = procedure?.category === 'clia_lab';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="procedure-doc-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-[540px] max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/60 shrink-0">
          <div>
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              {isClia ? <FlaskConical className="w-4 h-4 text-blue-500" /> : <Syringe className="w-4 h-4 text-purple-500" />}
              {procedure?.name || 'Document Procedure'}
            </h3>
            {task?.patient_name && (
              <p className="text-[10px] text-slate-400 mt-0.5">Patient: {task.patient_name}</p>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-200">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">

          {/* Procedure selector (if not pre-selected) */}
          {!procedure && (
            <div>
              <label className="text-[9px] text-slate-400 uppercase font-semibold tracking-wider">Select Procedure</label>
              <select onChange={e => {
                const p = catalog.find(c => c.id === e.target.value);
                if (p) selectProcedure(p);
              }} className="w-full mt-1 px-2.5 py-2 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20">
                <option value="">Choose from catalog...</option>
                {['injection','vaccine','clia_lab'].map(cat => (
                  <optgroup key={cat} label={cat === 'clia_lab' ? 'CLIA Labs' : cat.charAt(0).toUpperCase()+cat.slice(1)+'s'}>
                    {catalog.filter(p => p.category === cat).map(p => (
                      <option key={p.id} value={p.id}>{p.name} ({p.billing_code})</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          {procedure && (
            <>
              {/* Procedure info strip */}
              <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                <div className="flex-1">
                  <p className="text-xs font-semibold text-slate-700">{procedure.name}</p>
                  <p className="text-[10px] text-slate-400">{procedure.billing_code} · {procedure.description}</p>
                </div>
                <button onClick={() => setProcedure(null)}
                  className="text-[10px] text-slate-400 hover:text-slate-600 underline">Change</button>
              </div>

              {/* Already-signed notice */}
              {alreadySigned && (
                <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-amber-800">
                    This encounter was already signed. Documenting will create an <strong>addendum</strong> that
                    goes back to the physician's sign queue for co-signature.
                  </p>
                </div>
              )}

              {/* Injection / vaccine fields */}
              {isInjection && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[9px] text-slate-400 uppercase font-semibold">Dose Given</label>
                    <input value={form.dose_given} onChange={e => set('dose_given', e.target.value)}
                      placeholder={procedure.default_dose || 'e.g. 500mg'}
                      className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-400 uppercase font-semibold">Units</label>
                    <input type="number" min="1" value={form.units}
                      onChange={e => set('units', parseInt(e.target.value) || 1)}
                      className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-400 uppercase font-semibold">Route *</label>
                    <select value={form.route} onChange={e => set('route', e.target.value)}
                      className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
                      <option value="">Select route</option>
                      {(procedure.routes?.length ? procedure.routes : INJECTION_ROUTES).map(r => (
                        <option key={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[9px] text-slate-400 uppercase font-semibold">Injection Site *</label>
                    <select value={form.site} onChange={e => set('site', e.target.value)}
                      className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
                      <option value="">Select site</option>
                      {INJECTION_SITES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* CLIA lab result fields */}
              {isClia && procedure.result_fields?.length > 0 && (
                <div>
                  <p className="text-[9px] text-slate-400 uppercase font-semibold tracking-wider mb-2">Results</p>
                  <div className="grid grid-cols-2 gap-3">
                    {procedure.result_fields.map(field => (
                      <div key={field.field} className={field.field === 'color' || field.field.includes('result') ? 'col-span-1' : ''}>
                        <label className="text-[9px] text-slate-400 uppercase font-semibold">{field.label}</label>
                        {field.type === 'select' ? (
                          <select value={form.result_data[field.field] || ''}
                            onChange={e => setResult(field.field, e.target.value)}
                            className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none">
                            <option value="">Select...</option>
                            {(field.options || []).map(opt => <option key={opt}>{opt}</option>)}
                          </select>
                        ) : (
                          <input value={form.result_data[field.field] || ''}
                            onChange={e => setResult(field.field, e.target.value)}
                            placeholder={field.placeholder || ''}
                            className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lot number + expiration */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[9px] text-slate-400 uppercase font-semibold">
                    Lot Number {procedure.requires_lot ? '*' : ''}
                  </label>
                  <input value={form.lot_number} onChange={e => set('lot_number', e.target.value)}
                    placeholder="Enter lot #"
                    className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
                </div>
                <div>
                  <label className="text-[9px] text-slate-400 uppercase font-semibold">Expiration Date</label>
                  <input type="date" value={form.expiration_date}
                    onChange={e => set('expiration_date', e.target.value)}
                    className="w-full mt-0.5 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none" />
                </div>
              </div>

              {/* Nurse notes */}
              <div>
                <label className="text-[9px] text-slate-400 uppercase font-semibold">Nurse Notes (optional)</label>
                <textarea value={form.nurse_notes} onChange={e => set('nurse_notes', e.target.value)}
                  rows={2} placeholder="Any observations, patient tolerance, etc."
                  className="w-full mt-0.5 px-2.5 py-2 text-xs border border-slate-200 rounded-lg resize-none focus:outline-none" />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center gap-3 justify-end shrink-0">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-100">
            Cancel
          </button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting || !procedure}
            className="gap-1.5 text-xs h-8 bg-emerald-600 hover:bg-emerald-700"
            data-testid="submit-procedure-btn">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {submitting ? 'Documenting...' : 'Document & Drop Charge'}
          </Button>
        </div>
      </div>
    </div>
  );
}
